import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Copy, Upload, ArrowLeft, Loader2, Smartphone, Share2, Volume2, VolumeX } from 'lucide-react';
import { apiFetch, apiUpload } from '../lib/api';

const API_BASE = import.meta.env.VITE_API_URL || '';
import type { Donation, PaymentInstructions } from '../types';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import ShareModal from '../components/shared/ShareModal';

export default function DonationSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dn = searchParams.get('dn');

  const [donation, setDonation] = useState<Donation | null>(null);
  const [paymentInfo, setPaymentInfo] = useState<PaymentInstructions | null>(null);
  const [loading, setLoading] = useState(true);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [musicEnabled, setMusicEnabled] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const audio = new Audio('/music/ending.mp3');
    audio.loop = true;
    audio.volume = 0.3;
    audioRef.current = audio;
    return () => {
      audio.pause();
      audioRef.current = null;
    };
  }, []);

  const toggleMusic = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (musicEnabled) {
      audio.pause();
      setMusicEnabled(false);
    } else {
      audio.play().catch(() => {});
      setMusicEnabled(true);
    }
  };

  useEffect(() => {
    if (!dn) {
      setLoading(false);
      return;
    }
    (async () => {
      const [donationRes, paymentRes] = await Promise.all([
        apiFetch<Donation>(`/api/public/donations/${dn}`),
        apiFetch<PaymentInstructions>('/api/public/payment-info'),
      ]);
      if (donationRes.success && donationRes.data) {
        setDonation(donationRes.data);
      }
      if (paymentRes.success && paymentRes.data) {
        setPaymentInfo(paymentRes.data);
      }
      setLoading(false);
    })();
  }, [dn]);

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !dn) return;

    setUploading(true);
    setUploadResult('');
    const formData = new FormData();
    formData.append('proof', file);

    const res = await apiUpload(`/api/public/donations/${dn}/proof`, formData);
    setUploading(false);

    if (res.success) {
      setUploadResult('上傳成功！我們會盡快確認您的捐款。');
    } else {
      setUploadResult(res.error?.message || '上傳失敗，請重試');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-brand-600 animate-spin" />
      </div>
    );
  }

  if (!donation) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">找不到捐款記錄</p>
          <button onClick={() => navigate('/')} className="btn-primary">
            <ArrowLeft className="w-4 h-4" />
            返回首頁
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />
      <main className="py-12 px-4">
        <div className="container-max mx-auto max-w-xl">
          {/* Success Card */}
          <div className="card p-8 text-center mb-6">
            {/* Success animation */}
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-24 h-24 bg-green-200 rounded-full blur-xl opacity-40 animate-pulse" />
              </div>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto relative">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
            </div>

            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              感謝您！
            </h1>
            <p className="text-xl font-bold text-gray-900 mb-2">
              改寫了兩個人生
            </p>
            <p className="text-gray-500 mb-6">
              您的愛心將化作一頓頓溫飽的飯餐，溫暖每一個需要幫助的人
            </p>

            <div className="bg-brand-50 rounded-2xl p-6 mb-6 border border-orange-100">
              <div className="text-sm text-gray-500 mb-1">捐款編號</div>
              <div className="flex items-center justify-center gap-2 mb-3">
                <span className="text-xl font-bold text-brand-800">{donation.donationNum}</span>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(donation.donationNum);
                  }}
                  className="p-1.5 text-brand-600 hover:text-brand-700 hover:bg-brand-50 rounded-lg transition-colors"
                  title="複製捐款編號"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <div className="text-sm text-gray-500 mb-1">捐款金額</div>
              <div className="text-3xl font-bold text-brand-600">
                HK${(donation.amount / 100).toLocaleString()}
              </div>
              {donation.donationType.startsWith('monthly') && (
                <div className="text-sm text-brand-600 mt-1">每月定期捐款</div>
              )}
              {/* Impact breakdown */}
              <div className="mt-4 pt-4 border-t border-orange-200/60 text-sm text-gray-600 space-y-1">
                <div className="flex items-center justify-center gap-2">
                  <span>🍚</span>
                  <span>為基層人士提供 <strong className="text-brand-700">{Math.round(donation.amount / 100 / 50)}</strong> 份飯餐</span>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <span>👦</span>
                  <span>支持 SEN 學員 <strong className="text-brand-700">{Math.round(donation.amount / 100 / 50 * 0.5)}</strong> 小時職訓</span>
                </div>
              </div>
            </div>

            <div className="text-left space-y-4 mb-8">
              <h3 className="font-bold text-gray-800 text-center">付款指引</h3>

              {paymentInfo && donation.paymentMethod === 'fps' && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <div className="font-medium text-gray-700 mb-1">FPS 轉數快</div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-gray-900 font-mono text-lg">{paymentInfo.fpsId}</span>
                    <button
                      onClick={() => handleCopy(paymentInfo.fpsId, 'fpsId')}
                      className="text-brand-600 hover:text-brand-700 flex items-center gap-1 text-sm bg-white px-3 py-1.5 rounded-lg border border-orange-200 hover:border-orange-300 transition-colors"
                    >
                      {copiedField === 'fpsId' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      {copiedField === 'fpsId' ? '已複製' : '複製'}
                    </button>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">收款人：{paymentInfo.fpsName}</div>
                </div>
              )}

              {paymentInfo && donation.paymentMethod === 'payme' && (
                <div className="bg-gray-50 rounded-xl p-4 text-center border border-gray-100">
                  <div className="font-medium text-gray-700 mb-2">PayMe</div>
                  <a
                    href={paymentInfo.paymeLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary w-full"
                  >
                    <Smartphone className="w-5 h-5" />
                    開啟 PayMe 付款
                  </a>
                </div>
              )}

              {paymentInfo && donation.paymentMethod === 'bank-transfer' && (
                <div className="bg-gray-50 rounded-xl p-4 space-y-2 border border-gray-100">
                  <div className="font-medium text-gray-700 mb-1">銀行轉賬</div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">銀行</span>
                    <span className="text-gray-900 font-medium">{paymentInfo.bankName}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">帳號</span>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-900 font-mono">{paymentInfo.bankAccount}</span>
                      <button
                        onClick={() => handleCopy(paymentInfo.bankAccount, 'bankAccount')}
                        className="text-brand-600 hover:text-brand-700"
                      >
                        {copiedField === 'bankAccount' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">戶名</span>
                    <span className="text-gray-900 font-medium">{paymentInfo.accountName}</span>
                  </div>
                </div>
              )}

              {paymentInfo && (
                <div className="text-xs text-gray-500 text-center bg-yellow-50 p-3 rounded-lg border border-yellow-100">
                  ⚠️ 請在備註欄填寫捐款編號：<span className="font-bold text-gray-700">{donation.donationNum}</span>
                </div>
              )}
            </div>

            {/* 上傳付款證明 */}
            {donation?.paymentProofUrl ? (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  付款證明
                </label>
                <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
                  <div className="flex items-center gap-2 text-green-700 text-sm font-medium mb-3">
                    <CheckCircle className="w-4 h-4" />
                    已上傳付款證明，等待管理員核實
                  </div>
                  <img
                    src={`${API_BASE}/api/public/proofs/${donation.paymentProofUrl}`}
                    alt="付款證明"
                    className="w-full rounded-xl border border-green-100 mb-3"
                  />
                  <button
                    onClick={() => setShowShareModal(true)}
                    className="btn-primary w-full"
                  >
                    <Share2 className="w-5 h-5" />
                    分享我的善行
                  </button>
                </div>
              </div>
            ) : (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  上傳付款證明
                </label>
                {(!uploadResult || !uploadResult.includes('成功')) ? (
                  <>
                    <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-brand-200 rounded-2xl cursor-pointer hover:bg-brand-50 transition-colors bg-white">
                      <div className="flex items-center gap-2 text-brand-600">
                        {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                        <span className="text-sm font-medium">
                          {uploading ? '上傳中...' : '點擊上傳截圖或收據'}
                        </span>
                      </div>
                      <input type="file" accept="image/*" className="hidden" onChange={handleUpload} />
                    </label>
                    {uploadResult && (
                      <p className={`text-sm mt-2 ${uploadResult.includes('成功') ? 'text-green-600' : 'text-red-600'}`}>
                        {uploadResult}
                      </p>
                    )}
                  </>
                ) : (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <div className="flex items-start gap-2 text-green-700 mb-3">
                      <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <div>
                        {/* Mobile: 分兩行，第一行大2px粗體+flashing light */}
                        <div className="sm:hidden">
                          <p className="text-base font-bold animate-flashlight">上傳成功✅！</p>
                          <p className="text-sm font-medium">我們會盡快確認您的捐款。</p>
                        </div>
                        {/* Desktop: 一行 */}
                        <p className="text-sm font-medium hidden sm:block">
                          上傳成功！我們會盡快確認您的捐款。
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowShareModal(true)}
                      className="btn-primary w-full"
                    >
                      <Share2 className="w-5 h-5" />
                      分享我的善行
                    </button>
                  </div>
                )}
              </div>
            )}

            <button onClick={() => navigate('/')} className="btn-secondary w-full">
              <ArrowLeft className="w-4 h-4" />
              返回首頁
            </button>
          </div>
        </div>
      </main>
      {showShareModal && donation && (
        <ShareModal
          amount={donation.amount / 100}
          meals={Math.round(donation.amount / 100 / 50)}
          donationType={donation.donationType}
          onClose={() => setShowShareModal(false)}
        />
      )}
      <button
        onClick={toggleMusic}
        className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-colors bg-white border border-gray-200 hover:bg-gray-50"
        title={musicEnabled ? '關閉背景音樂' : '播放背景音樂'}
      >
        {musicEnabled ? <Volume2 className="w-5 h-5 text-brand-600" /> : <VolumeX className="w-5 h-5 text-gray-400" />}
      </button>
      <Footer />
    </div>
  );
}
